import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  SafeAreaView, StatusBar, Alert, RefreshControl, TextInput, Modal,
  FlatList,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const API = 'https://api.jobeng.ai';
const KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const P = '#0e4e96';
const getH = async () => {
  const t = await AsyncStorage.getItem('auth_token').catch(() => null);
  return { 'Content-Type': 'application/json', 'x-api-key': KEY, ...(t ? { Authorization: `Bearer ${t}` } : {}) };
};

const PRIORITY_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#10b981' };
const STATUS_COLORS = { active: P, completed: '#10b981', paused: '#f59e0b', cancelled: '#94a3b8' };

export default function TaskListScreen({ navigation, route }) {
  const empId = route?.params?.employeeId || route?.params?.employee?.id || '1';
  const [goals, setGoals] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', target: '', dueDate: '', priority: 'medium' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const h = await getH();
      const res = await fetch(`${API}/api/goals/${parseInt(empId)||0}`, { headers: h });
      const json = await res.json().catch(() => ({}));
      setGoals(Array.isArray(json?.data) ? json.data : []);
    } catch (e) { console.warn('TaskList load:', e.message); }
    finally { setLoading(false); setRefreshing(false); }
  }, [empId]);

  useEffect(() => { load(); }, [load]);

  const addGoal = async () => {
    if (!form.title.trim()) { Alert.alert('Required', 'Please enter a goal title'); return; }
    setSaving(true);
    try {
      const h = await getH();
      const res = await fetch(`${API}/api/goals`, {
        method: 'POST', headers: h,
        body: JSON.stringify({ employeeId: empId, ...form }),
      });
      const json = await res.json().catch(() => ({}));
      if (json.success || res.ok) {
        Alert.alert('✅ Goal Added');
        setShowAdd(false);
        setForm({ title: '', description: '', target: '', dueDate: '', priority: 'medium' });
        load();
      } else Alert.alert('Error', json.error || 'Could not add goal');
    } catch (e) { Alert.alert('Error', e.message); }
    finally { setSaving(false); }
  };

  const updateProgress = async (goalId, progress) => {
    try {
      const h = await getH();
      await fetch(`${API}/api/goals/${goalId}/progress`, {
        method: 'PUT', headers: h,
        body: JSON.stringify({ progress }),
      });
      load();
    } catch (e) { console.warn('Progress update:', e.message); }
  };

  const filtered = filter === 'all' ? goals : goals.filter(g => g.status === filter);
  const completed = goals.filter(g => g.status === 'completed').length;
  const active = goals.filter(g => g.status === 'active').length;

  return (
    <SafeAreaView style={s.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P} />
      <LinearGradient colors={[P, '#1e7be2']} style={s.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={s.back}>
          <MaterialCommunityIcons name="arrow-left" size={22} color="#fff" />
        </TouchableOpacity>
        <Text style={s.headerTitle}>Goals & Tasks</Text>
        <TouchableOpacity onPress={() => setShowAdd(true)} style={s.addBtn}>
          <MaterialCommunityIcons name="plus" size={22} color="#fff" />
        </TouchableOpacity>
      </LinearGradient>

      <ScrollView refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} colors={[P]} />}>
        {/* Summary */}
        <View style={s.summaryRow}>
          <View style={s.summaryCard}>
            <Text style={[s.summaryNum, { color: P }]}>{active}</Text>
            <Text style={s.summaryLabel}>Active</Text>
          </View>
          <View style={s.summaryCard}>
            <Text style={[s.summaryNum, { color: '#10b981' }]}>{completed}</Text>
            <Text style={s.summaryLabel}>Completed</Text>
          </View>
          <View style={s.summaryCard}>
            <Text style={[s.summaryNum, { color: '#1e293b' }]}>{goals.length}</Text>
            <Text style={s.summaryLabel}>Total</Text>
          </View>
          {goals.length > 0 && (
            <View style={s.summaryCard}>
              <Text style={[s.summaryNum, { color: '#f59e0b' }]}>
                {Math.round(goals.reduce((s, g) => s + (parseInt(g.progress) || 0), 0) / goals.length)}%
              </Text>
              <Text style={s.summaryLabel}>Avg Progress</Text>
            </View>
          )}
        </View>

        {/* Filter Chips */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.filters} contentContainerStyle={{ paddingHorizontal: 16, gap: 8 }}>
          {['all', 'active', 'completed', 'paused'].map(f => (
            <TouchableOpacity key={f} onPress={() => setFilter(f)}
              style={[s.filterChip, filter === f && s.filterChipActive]}>
              <Text style={[s.filterText, filter === f && s.filterTextActive]}>{f.charAt(0).toUpperCase() + f.slice(1)}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* Goals */}
        <View style={s.list}>
          {filtered.length === 0 && !loading && (
            <View style={s.emptyBox}>
              <MaterialCommunityIcons name="flag-outline" size={48} color="#cbd5e1" />
              <Text style={s.emptyText}>No goals yet</Text>
              <TouchableOpacity style={s.emptyBtn} onPress={() => setShowAdd(true)}>
                <Text style={s.emptyBtnText}>Add First Goal</Text>
              </TouchableOpacity>
            </View>
          )}
          {filtered.map((g, i) => (
            <TouchableOpacity key={g.id || i} style={s.goalCard}
              onPress={() => navigation.navigate('TaskDetails', { task: g, employeeId: empId, onUpdate: load })}>
              <View style={s.goalTop}>
                <View style={[s.priorityDot, { backgroundColor: PRIORITY_COLORS[g.priority] || '#94a3b8' }]} />
                <Text style={s.goalTitle} numberOfLines={2}>{g.title}</Text>
                <View style={[s.statusChip, { backgroundColor: (STATUS_COLORS[g.status] || '#94a3b8') + '20' }]}>
                  <Text style={[s.statusText, { color: STATUS_COLORS[g.status] || '#94a3b8' }]}>
                    {(g.status || 'active').toUpperCase()}
                  </Text>
                </View>
              </View>
              {g.description ? <Text style={s.goalDesc} numberOfLines={1}>{g.description}</Text> : null}
              <View style={s.progressRow}>
                <View style={s.progressBar}>
                  <View style={[s.progressFill, { width: `${parseInt(g.progress) || 0}%` }]} />
                </View>
                <Text style={s.progressText}>{parseInt(g.progress) || 0}%</Text>
              </View>
              {g.due_date && (
                <Text style={s.dueDate}>
                  <MaterialCommunityIcons name="calendar" size={11} color="#94a3b8" /> Due {new Date(g.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </Text>
              )}
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>

      <Modal visible={showAdd} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={{ flex: 1, backgroundColor: '#f8fafc' }}>
          <View style={s.modalHeader}>
            <TouchableOpacity onPress={() => setShowAdd(false)}><MaterialCommunityIcons name="close" size={22} color="#1e293b" /></TouchableOpacity>
            <Text style={s.modalTitle}>New Goal</Text>
            <TouchableOpacity onPress={addGoal} disabled={saving}>
              <Text style={[s.saveText, saving && { opacity: 0.5 }]}>{saving ? 'Saving...' : 'Save'}</Text>
            </TouchableOpacity>
          </View>
          <ScrollView style={{ padding: 16 }}>
            {[['Title *', 'title', 'e.g. Complete React certification'], ['Description', 'description', 'What does success look like?'], ['Target / Metric', 'target', 'e.g. Score 90% on exam'], ['Due Date', 'dueDate', 'YYYY-MM-DD']].map(([label, field, ph]) => (
              <View key={field}>
                <Text style={s.fieldLabel}>{label}</Text>
                <TextInput style={s.input} value={form[field]} onChangeText={v => setForm(p => ({ ...p, [field]: v }))} placeholder={ph} multiline={field === 'description'} />
              </View>
            ))}
            <Text style={s.fieldLabel}>Priority</Text>
            <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
              {['low', 'medium', 'high'].map(p => (
                <TouchableOpacity key={p} onPress={() => setForm(pr => ({ ...pr, priority: p }))}
                  style={[s.chip, form.priority === p && { backgroundColor: PRIORITY_COLORS[p], borderColor: PRIORITY_COLORS[p] }]}>
                  <Text style={[s.chipText, form.priority === p && { color: '#fff' }]}>{p}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#f8fafc' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14 },
  back: { padding: 4 }, addBtn: { padding: 4 },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: '700' },
  summaryRow: { flexDirection: 'row', padding: 16, gap: 8 },
  summaryCard: { flex: 1, backgroundColor: '#fff', borderRadius: 12, padding: 12, alignItems: 'center', shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 4, elevation: 1 },
  summaryNum: { fontSize: 24, fontWeight: '900' },
  summaryLabel: { fontSize: 10, color: '#64748b', fontWeight: '600', marginTop: 2 },
  filters: { marginBottom: 8 },
  filterChip: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 20, borderWidth: 1.5, borderColor: '#e2e8f0', backgroundColor: '#fff' },
  filterChipActive: { backgroundColor: P, borderColor: P },
  filterText: { fontSize: 12, fontWeight: '600', color: '#64748b' },
  filterTextActive: { color: '#fff' },
  list: { padding: 16, gap: 10 },
  emptyBox: { alignItems: 'center', padding: 40 },
  emptyText: { color: '#94a3b8', fontSize: 14, marginTop: 8 },
  emptyBtn: { marginTop: 16, backgroundColor: P, paddingHorizontal: 24, paddingVertical: 10, borderRadius: 12 },
  emptyBtnText: { color: '#fff', fontWeight: '700' },
  goalCard: { backgroundColor: '#fff', borderRadius: 16, padding: 16, shadowColor: '#000', shadowOpacity: 0.05, shadowRadius: 6, elevation: 2 },
  goalTop: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, marginBottom: 6 },
  priorityDot: { width: 10, height: 10, borderRadius: 5, marginTop: 3 },
  goalTitle: { flex: 1, fontSize: 14, fontWeight: '700', color: '#1e293b' },
  statusChip: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
  statusText: { fontSize: 9, fontWeight: '800' },
  goalDesc: { fontSize: 12, color: '#64748b', marginBottom: 8 },
  progressRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  progressBar: { flex: 1, height: 6, backgroundColor: '#e2e8f0', borderRadius: 3 },
  progressFill: { height: 6, borderRadius: 3, backgroundColor: P },
  progressText: { fontSize: 12, fontWeight: '700', color: P, width: 36 },
  dueDate: { fontSize: 11, color: '#94a3b8', marginTop: 6 },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  modalTitle: { fontSize: 16, fontWeight: '700', color: '#1e293b' },
  saveText: { color: P, fontWeight: '700', fontSize: 15 },
  fieldLabel: { fontSize: 12, fontWeight: '700', color: '#374151', marginBottom: 6 },
  input: { backgroundColor: '#fff', borderRadius: 10, borderWidth: 1, borderColor: '#e2e8f0', padding: 12, marginBottom: 16, fontSize: 14, color: '#1e293b' },
  chip: { paddingHorizontal: 18, paddingVertical: 8, borderRadius: 20, borderWidth: 1.5, borderColor: '#e2e8f0', backgroundColor: '#fff' },
  chipText: { fontSize: 13, fontWeight: '600', color: '#64748b' },
});
